import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { DefectTypeDistribution as DefectTypeDistributionType } from '../../types/stats';

interface DefectTypeDistributionProps {
  data: DefectTypeDistributionType[];
}

export const DefectTypeDistribution = ({ data }: DefectTypeDistributionProps) => {
  const option = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    // Sort by count descending and take top 10
    const sortedData = [...data].sort((a, b) => b.count - a.count).slice(0, 10);
    const types = sortedData.map((item) => item.type);
    const counts = sortedData.map((item) => item.count);
    const total = data.reduce((sum, item) => sum + item.count, 0);

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const percentage = ((params.value / total) * 100).toFixed(1);
          return `${params.name}<br/>Count: ${params.value}<br/>Percentage: ${percentage}%`;
        },
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        top: 'middle',
        type: 'scroll',
      },
      series: [
        {
          name: 'Defect Types',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: true,
            formatter: (params: any) => {
              const percentage = ((params.value / total) * 100).toFixed(1);
              return `${params.name}\n${percentage}%`;
            },
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold',
            },
          },
          data: sortedData.map((item) => ({
            value: item.count,
            name: item.type,
          })),
        },
      ],
    } as echarts.EChartsOption;
  }, [data]);

  if (!option) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No defect type data available.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Defect Type Distribution</h3>
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Top 10 Defect Types</p>
      </div>
      <ReactECharts 
        option={option} 
        style={{ height: 450 }} 
        key="defect-type-distribution"
        notMerge={true}
        lazyUpdate={true}
      />
    </div>
  );
};

